/**
 * پاک کردنِ بخشی از داده‌ها، بدونِ بستنِ حساب (WHOSUP-169).
 *
 * دو متد: چقدر از هر چیز مانده (`counts`) و پاک کن (`erase`). فهرست و
 * ترتیب و متن‌ها در `lib/dataErase.ts` است؛ این‌جا فقط دیتابیس.
 *
 * ═══ چرا هیچ‌کدام RPCِ تازه نیست ═══
 *
 * برخلافِ `delete_my_account` (مایگریشن 0046) که بیست جدول را در یک تراکنش
 * می‌برد، هر کارِ این‌جا **یک** چیز است و سیاستِ RLS خودش را از قبل دارد:
 * `profiles` فقط ردیفِ خود کاربر، `uck_del` فقط کلیدهای خودش، `ct_all` فقط
 * دفترچه‌ی خودش. نیمه‌کاره ماندنشان هم بی‌ضرر است — پاک شدنِ دو تا از سه
 * شماره یعنی دو شماره پاک شده، نه یک حسابِ ناسازگار. پس مایگریشنی لازم
 * نیست و هر کار همان مسیرِ عادیِ خودش را می‌رود.
 */
import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { demoStore } from '../demo/store';
import { profileRepo } from './socialRepo';
import { phoneRepo } from './phoneRepo';
import { contactFriendRepo } from './contactFriendRepo';
import type { EraseCounts, ErasableKind } from '../lib/dataErase';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** شناسه‌ی کاربرِ واردشده، یا خطا. هر دو متد پشتِ نشست‌اند. */
async function myId(): Promise<string> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error('not signed in');
  return auth.user.id;
}

/**
 * سه تابعِ دسته‌جمعیِ مایگریشن 0136، که همه یک شکلِ جواب دارند.
 *
 * جوابشان (`{cancelled, deleted}`) امروز خوانده نمی‌شود — رابط بعدش
 * شمارش‌ها را دوباره می‌گیرد و همان حقیقتِ تازه را نشان می‌دهد. ولی نوعش
 * این‌جا نوشته شده چون همان چیزی است که اگر روزی بخواهیم بگوییم «۲ تا لغو
 * شد، ۵ تا رفت» لازم است، و حدس زدنش از روی نامِ تابع ممکن نیست.
 */
async function rpcErase(fn: 'erase_my_signals' | 'erase_my_events' | 'erase_my_messages'):
Promise<{ cancelled: number; deleted: number }> {
  const { data, error } = await supabase.rpc(fn);
  if (error) throw error;
  const r = (data ?? {}) as { cancelled?: number; deleted?: number };
  return { cancelled: Number(r.cancelled ?? 0), deleted: Number(r.deleted ?? 0) };
}

export const dataEraseRepo = {
  /**
   * چقدر از هر نوع داده مانده.
   *
   * عکس و متنِ وضعیت با یک کوئری از `profiles` می‌آیند (نه از
   * `profileRepo.me`): آن یکی کلِ ردیف و آدرسِ عمومیِ عکس را می‌سازد، و
   * این‌جا فقط «هست یا نیست» لازم است.
   */
  async counts(): Promise<EraseCounts> {
    if (isDemoMode) {
      const p = demoStore.profile.me();
      return {
        avatar: p.avatarPath ? 1 : 0,
        phones: demoStore.contacts.myPhones().length,
        status: p.statusText?.trim() ? 1 : 0,
        contacts: demoStore.contacts.importedCount(),
        signals: demoStore.erase.signalCount(),
        events: demoStore.erase.eventCount(),
        messages: demoStore.erase.messageCount(),
      };
    }
    const uid = await myId();
    const [prof, phones, contacts, signals, events, messages] = await Promise.all([
      supabase.from('profiles').select('avatar_path, status_text').eq('id', uid).maybeSingle(),
      phoneRepo.mine(),
      supabase.from('contacts')
        .select('id', { count: 'exact', head: true })
        .eq('owner_id', uid).eq('source', 'device'),
      /**
       * دقیقاً همان شرطی که `erase_my_signals` رویش می‌دود (مایگریشن 0136).
       * سیگنالِ از-قبل-لغو‌شده شمرده نمی‌شود چون آن تابع هم دستش نمی‌زند —
       * وگرنه ردیف «۳ سیگنال» می‌گفت و دکمه هیچ کاری نمی‌کرد.
       */
      supabase.from('signals')
        .select('id', { count: 'exact', head: true })
        .eq('creator_id', uid).is('deleted_at', null).neq('status', 'cancelled'),
      supabase.from('events')
        .select('id', { count: 'exact', head: true })
        .eq('submitted_by', uid).in('status', ['published', 'pending_review', 'rejected']),
      supabase.from('messages')
        .select('id', { count: 'exact', head: true }).eq('sender_id', uid),
    ]);
    if (prof.error) throw prof.error;
    if (contacts.error) throw contacts.error;
    if (signals.error) throw signals.error;
    if (events.error) throw events.error;
    if (messages.error) throw messages.error;
    const row = prof.data as any;
    return {
      avatar: row?.avatar_path ? 1 : 0,
      phones: phones.length,
      status: String(row?.status_text ?? '').trim() ? 1 : 0,
      contacts: contacts.count ?? 0,
      signals: signals.count ?? 0,
      events: events.count ?? 0,
      messages: messages.count ?? 0,
    };
  },

  /**
   * همان یک چیز را پاک می‌کند، همین حالا.
   *
   * هیچ‌کدام «درخواستِ حذف» نیست و هیچ‌کدام صفی ندارد — همان تفاوتی که
   * فرمِ گوگل بینِ «کاربر می‌تواند بخواهد» و «کاربر می‌تواند انجام دهد»
   * می‌گذارد.
   */
  async erase(kind: ErasableKind): Promise<void> {
    if (isDemoMode) {
      if (kind === 'avatar') demoStore.profile.updateMine({ avatarPath: null });
      if (kind === 'status') demoStore.profile.updateMine({ statusText: null });
      if (kind === 'phones') {
        for (const p of demoStore.contacts.myPhones()) demoStore.contacts.removePhone(p.value);
      }
      if (kind === 'contacts') demoStore.contacts.removeImported();
      if (kind === 'signals') demoStore.erase.signals();
      if (kind === 'events') demoStore.erase.events();
      if (kind === 'messages') demoStore.erase.messages();
      return;
    }
    const uid = await myId();
    switch (kind) {
      case 'avatar': {
        // ترتیب مهم است: اول فایل، بعد ستون. برعکسش اگر وسطش قطع شود مسیرِ
        // فایل را از دست داده‌ایم و عکس تا ابد در باکت می‌ماند.
        const { data, error } = await supabase
          .from('profiles').select('avatar_path').eq('id', uid).maybeSingle();
        if (error) throw error;
        await profileRepo.removeAvatar((data as any)?.avatar_path ?? null);
        await profileRepo.updateMine(uid, { avatarPath: null });
        return;
      }
      case 'phones': {
        // پشتِ سرِ هم و نه با هم: حذفِ هر شماره تریگرِ «پیش‌فرضِ بعدی»
        // (مایگریشن 0048) را می‌زند، و همزمان زدنشان یعنی چند تریگر روی یک
        // مجموعه‌ی در حالِ تغییر. تعدادشان هم یکی دو تاست.
        for (const p of await phoneRepo.mine()) await phoneRepo.remove(p.value);
        return;
      }
      case 'status':
        await profileRepo.updateMine(uid, { statusText: null });
        return;
      case 'contacts':
        await contactFriendRepo.removeAllImported();
        return;
      /**
       * سه‌تای آخر هرکدام یک RPC‌اند و نه چند درخواست (مایگریشن 0136).
       *
       * دلیلش همان دلیلِ `delete_my_account` است: این‌ها چند ردیف در چند
       * جدول را دست می‌زنند و بینشان اعلان هم می‌رود. نصفه ماندنشان یعنی
       * سیگنالی که لغو شده ولی کسی خبردار نشده.
       */
      case 'signals':
        await rpcErase('erase_my_signals');
        return;
      case 'events':
        await rpcErase('erase_my_events');
        return;
      case 'messages':
        await rpcErase('erase_my_messages');
        return;
      default:
        // نوعِ ناشناخته یعنی رابط چیزی می‌فرستد که این‌جا پیاده نشده —
        // بی‌صدا رد شدن از کنارش همان «دکمه‌ای که کار نمی‌کند» است.
        throw new Error(`unknown erasable kind: ${String(kind)}`);
    }
  },
};
